import {
  MemCommonEditor,
  MemCommonEditorInstance,
  MemCommonEditorTheme,
  MemCommonEditorAssetProxyConfig,
} from "@mem-labs/common-editor";
import { FC, useRef, useMemo } from "react";
import {
  createReadonlyArtifactEditorConfig,
  NoteInfo,
} from "./session-artifact-note-renderer.utils";
import styled from "@emotion/styled";
import {
  MDSNotePreviewContainer,
  MDSNotePreviewContainerVariant,
} from "@/mds/components/note-preview-container";
import { ThemeType, useTheme } from "@/mds/themes";

export type SessionArtifactNoteRenderingData = {
  noteInfo: NoteInfo;
  previousNoteInfo?: NoteInfo;
  showDifferences?: boolean;
};

export type SessionArtifactNotePreviewRendererProps = {
  data: SessionArtifactNoteRenderingData;
  onClick: React.MouseEventHandler<HTMLDivElement>;
  resolveAssetProxy?: () => Promise<MemCommonEditorAssetProxyConfig | undefined>;
};

export const SessionArtifactNotePreviewRenderer: FC<SessionArtifactNotePreviewRendererProps> = ({
  data,
  onClick,
  resolveAssetProxy,
}) => {
  const { noteInfo, previousNoteInfo, showDifferences } = data;
  const { themeType } = useTheme();
  const editorInstanceRef = useRef<MemCommonEditorInstance | null>(null);

  const title = noteInfo.primaryLabel;
  const secondaryLabel = noteInfo.secondaryLabel;
  const createdAt = noteInfo.createdAt;
  const updatedAt = noteInfo.updatedAt;

  const updateOnly = showDifferences ?? false;
  const theme =
    themeType === ThemeType.Harvest
      ? MemCommonEditorTheme.Harvest
      : MemCommonEditorTheme.HarvestMoon;

  const editorConfig = useMemo(
    () =>
      createReadonlyArtifactEditorConfig({
        noteInfo,
        previousNoteInfo,
        showDifferences,
        theme,
        resolveAssetProxy,
      }),
    [noteInfo, previousNoteInfo, showDifferences, theme, resolveAssetProxy]
  );

  return (
    <StyledMDSNotePreviewContainer
      title={title}
      createdAt={createdAt.toJSDate()}
      modifiedAt={updatedAt.toJSDate()}
      authorNames={[]}
      secondaryLabel={secondaryLabel}
      updateOnly={updateOnly}
      width="300px"
      variant={MDSNotePreviewContainerVariant.Flat}
      onClick={onClick}
      shouldScroll={false}
    >
      <StyledMemCommonEditor
        uiTheme={theme}
        editorInstanceRef={editorInstanceRef}
        editorInitializer={editorConfig.editorInitializer}
        editorEventHandler={editorConfig.editorEventHandler}
      />
    </StyledMDSNotePreviewContainer>
  );
};

const StyledMDSNotePreviewContainer = styled(MDSNotePreviewContainer)`
  margin-top: ${({ theme }) => theme.spacing["0.5"]};
`;

const StyledMemCommonEditor = styled(MemCommonEditor)`
  overflow-y: hidden;
  scrollbar-width: none;
  pointer-events: none;

  ::-webkit-scrollbar {
    display: none;
  }

  .tiptap {
    padding: 16px;
  }
`;
